import React from 'react';
import { Control, FieldErrors } from 'react-hook-form';
import { DarwinCol, DarwinRow } from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { UnlockOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from '../../Products.module.css';
import { TreeNode } from 'types/productsTypes';
import { InputType } from 'types/genericTypes';
import { GenericInput } from '../../../GenericInput';
import {
  PRODUCT_NAME_MAX_LENGTH,
  PRODUCT_DESCRIPTION_MAX_LENGTH,
  PRODUCT_SKU_MAX_LENGTH,
  PRODUCT_GTIN_MAX_LENGTH,
  PRODUCT_ID_MAX_LENGTH,
} from 'utils/product';
import {
  ProductInputProps,
  CreateProductWithoutFormData,
} from 'types/productsTypes';

const { Title } = DarwinTypography;

interface ProductInformationSectionProps {
  control: Control<CreateProductWithoutFormData>;
  errors: FieldErrors<CreateProductWithoutFormData>;
  productDataCategory: TreeNode[];
  isViewForm: boolean;
}

export const ProductInformationSection: React.FC<
  ProductInformationSectionProps
> = ({ control, errors, productDataCategory, isViewForm }) => {
  const { t } = useTranslation();

  const firstColumnInputs: ProductInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'productName',
      label: t('product.new-product-form.product-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('product.new-product-form.product-name-required'),
        maxLength: {
          value: PRODUCT_NAME_MAX_LENGTH,
          message: t('product.new-product-form.max-length-error', {
            fieldName: t('product.new-product-form.product-name'),
            maxLength: PRODUCT_NAME_MAX_LENGTH,
          }),
        },
      },
      maxLength: PRODUCT_NAME_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'traceabilityProductCode',
      label: t('product.new-product-form.traceability-product-code'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: true,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'gtin',
      label: t('product.new-product-form.gtin'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: PRODUCT_GTIN_MAX_LENGTH,
          message: t('product.new-product-form.max-length-error', {
            fieldName: t('product.new-product-form.gtin'),
            maxLength: PRODUCT_GTIN_MAX_LENGTH,
          }),
        },
      },
      maxLength: PRODUCT_GTIN_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('product.new-product-form.description'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: PRODUCT_DESCRIPTION_MAX_LENGTH,
          message: t('product.new-product-form.max-length-error', {
            fieldName: t('product.new-product-form.description'),
            maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH,
          }),
        },
      },
      maxLength: PRODUCT_DESCRIPTION_MAX_LENGTH,
      disabled: isViewForm,
    },
  ];

  const secondColumnInputs: ProductInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'externalId',
      label: t('product.new-product-form.product-id'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: PRODUCT_ID_MAX_LENGTH,
          message: t('product.new-product-form.max-length-error', {
            fieldName: t('product.new-product-form.product-id'),
            maxLength: PRODUCT_ID_MAX_LENGTH,
          }),
        },
      },
      maxLength: PRODUCT_ID_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.INPUT,
      fieldName: 'sku',
      label: t('product.new-product-form.sku'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: PRODUCT_SKU_MAX_LENGTH,
          message: t('product.new-product-form.max-length-error', {
            fieldName: t('product.new-product-form.sku'),
            maxLength: PRODUCT_SKU_MAX_LENGTH,
          }),
        },
      },
      maxLength: PRODUCT_SKU_MAX_LENGTH,
      disabled: isViewForm,
    },
    {
      inputType: InputType.TREESELECT,
      fieldName: 'category',
      label: t('product.new-product-form.category'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('product.new-product-form.product-category-required'),
      },
      dropdownTreeData: productDataCategory,
      disabled: isViewForm,
      enableSearchInDropdown: true,
    },
    {
      inputType: InputType.SWITCH,
      fieldName: 'finalProduct',
      label: t('product.new-product-form.final-product'),
      showRequiredAsterisk: false,
      inputRules: {},
      disabled: isViewForm,
    },
  ];

  return (
    <>
      <Title level={5} className={styles.productFormRowTitle}>
        <UnlockOutlined /> {t('product.new-product-form.product-information')}
      </Title>

      <DarwinRow className={styles.productFormRow} gutter={16}>
        <DarwinCol span={12}>
          {firstColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
        <DarwinCol span={12}>
          {secondColumnInputs.map(input => (
            <GenericInput
              key={input.fieldName}
              control={control}
              errors={errors}
              {...input}
            />
          ))}
        </DarwinCol>
      </DarwinRow>
    </>
  );
};
